import React from 'react'
import '../../css/matchesList.css'
import redBallLogo from '../../media/red-ball.png'
/**
 * MatchesCompact.jsx
 *
 * Renders match cards styled to mimic the screenshot format.
 * - Title: tournament name
 * - Meta: ground, city, date (DD-MMM-YY), overs, PAST pill
 * - Round label (uppercase)
 * - Two rows: Team A (left) score (right), Team B (left) score (right)
 *   - First token of team name is highlighted (tag-style)
 *   - Winning team is colored green
 * - Footer: "<Winner> won by <margin>"
 *
 * Data source: JSON array at DATA_URL (same structure you already have).
 */

const DATA_URL = '/data/matches.json' // change if needed

export default function MatchesCompact() {
  const [rows, setRows] = React.useState([])
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`${DATA_URL}?v=${Date.now()}`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const json = await res.json()
        if (!cancelled) setRows(Array.isArray(json) ? json : [])
      } catch (e) {
        if (!cancelled) setError(e.message || String(e))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className='container my-4'>
      <div className='row'>
        <div className='col-md-12 text-center mb-4'>
          <div className='top-heading m-auto'>
            <img src={redBallLogo} alt='Red Ball Logo' />
            <span> WHAT'S TRENDING</span>
          </div>
          <h2> Matches Result at 2025 NCCA-UCCL Labor Day T20 Tournament</h2>
        </div>
      </div>
      <div className='matches-grid'>
        {error && (
          <div className='match-card compact' style={{ color: '#b91c1c' }}>
            Failed to load: {error}
          </div>
        )}
        {rows.map(m => (
          <MatchCardCompact key={m.match_id} m={m} />
        ))}
      </div>
    </div>
  )
}

function MatchCardCompact({ m }) {
  const winner = (m?.winning_team || '').trim()
  const teamAIsWinner = winner && winner === m?.team_a
  const teamBIsWinner = winner && winner === m?.team_b

  const aOver = oversOf(m?.team_a_innings?.[0])
  const bOver = oversOf(m?.team_b_innings?.[0])

  return (
    <div className='match-card compact'>
      {/* Title */}
      <div className='card-title'>{m?.tournament_name || '—'}</div>

      {/* Meta row */}
      <div className='card-meta'>
        <span>{m?.ground_name}</span>, <span>{m?.city_name}</span>,{' '}
        <span>{formatDate(m?.match_start_time)}</span>, <span>{m?.overs} Ov.,</span>
      </div>

      {/* Round */}
      {m?.tournament_round_name ? (
        <div className='card-round'>{String(m.tournament_round_name).toUpperCase()}</div>
      ) : null}

      {/* Teams */}
      <div className='team-row'>
        <div className={`team-left ${teamAIsWinner ? 'win-text' : ''}`}>
          <TeamLabel name={m?.team_a} logo={m?.team_a_logo} />
        </div>
        <div className={`team-right score ${teamAIsWinner ? 'win' : ''}`}>
          {m?.team_a_summary} {aOver && <span className='overs'>({aOver})</span>}
        </div>
      </div>

      <div className='team-row'>
        <div className={`team-left ${teamBIsWinner ? 'win-text' : ''}`}>
          <TeamLabel name={m?.team_b} logo={m?.team_b_logo} />
        </div>
        <div className={`team-right score ${teamBIsWinner ? 'win' : ''}`}>
          {m?.team_b_summary} {bOver && <span className='overs'>({bOver})</span>}
        </div>
      </div>

      {/* Footer */}
      <div className='card-footer'>
        <b>{winner}</b> {m?.win_by ? `won by ${m.win_by}` : m?.match_summary?.summary || ''}
      </div>
    </div>
  )
}

function TeamLabel({ name, logo }) {
  const parts = String(name || '')
    .split(/\s+/)
    .filter(Boolean)
  const first = parts.shift()
  return (
    <div className='team-label' style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Logo src={logo} alt={`${name || 'Team'} logo`} initials={initialsOf(name)} />
      <span className='team-name'>
        {first && <span>{first}</span>} {parts.join(' ')}
      </span>
    </div>
  )
}

function Logo({ src, alt, initials = 'T' }) {
  const [broken, setBroken] = React.useState(false)
  if (!src || broken) {
    return (
      <div className='logo-fallback' title={alt}>
        {initials}
      </div>
    )
  }
  return <img className='team-logo' src={src} alt={alt} onError={() => setBroken(true)} />
}

function initialsOf(name) {
  if (!name) return 'T'
  const words = String(name).split(/\s+/).filter(Boolean)
  return (words[0]?.[0] || 'T').toUpperCase()
}

// ——— helpers ——————————————————————————————————————————
function highlightFirstToken(name) {
  if (!name) return <span>—</span>
  const [first, ...rest] = String(name).split(/\s+/).filter(Boolean)
  return (
    <span className='team-name'>
      {first && <span className='tag'>{first}</span>} {rest.join(' ')}
    </span>
  )
}

function oversOf(inning) {
  if (!inning) return ''
  if (inning.overs_played) return String(inning.overs_played)
  const s = inning.summary?.over // e.g., "(19.5 Ov)"
  if (!s) return ''
  const match = s.match(/\(([^\s)]+)\s*Ov\)/i)
  return match ? match[1] : ''
}

function formatDate(iso) {
  try {
    const d = new Date(iso)
    if (isNaN(d)) return iso || ''
    const day = String(d.getDate()).padStart(2, '0')
    const mon = d.toLocaleString('en-US', { month: 'short' })
    const yr = String(d.getFullYear()).slice(-2)
    return `${day}-${mon}-${yr}` // 30-Aug-25
  } catch {
    return iso || ''
  }
}
